import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Grid } from '@mui/material';
import api from '../../services/api';
import EntityPickerInline from '../ticket/EntityPickerInline';
import CustomFieldInput from '../authentication/configuracoes/shared/CustomFieldInput.js';
import { isFieldVisible } from '../authentication/configuracoes/shared/fieldVisibility';

// Criação de negócio a partir do quadro Kanban (`PipelineMain.js`) - mesmo
// padrão de campos/endpoints usado no resto do sistema pra criar um
// negócio (ver `DadosDealDrawer.js::createDeal`): título/valor/descrição +
// Cliente/Organização/Atribuído buscados de verdade (não texto livre) +
// campos personalizados do pipeline. Isso é o que faltava aqui - antes só
// existia um campo de texto solto "Contato", sem vincular nenhum Cliente
// de verdade.
const NewDeal = ({ pipelineId, firstStageId, onClose, onSaved }) => {
    const [form, setForm] = useState({ title: '', value: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [appId, setAppId] = useState(null);
    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});

    const [clienteSelecionado, setClienteSelecionado] = useState(null);
    const [organizacaoSelecionada, setOrganizacaoSelecionada] = useState(null);
    const [atribuidoSelecionado, setAtribuidoSelecionado] = useState(null);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    useEffect(() => {
        api.get('v1/apps/default/')
            .then((response) => setAppId(response.data.app.id))
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (!appId || !pipelineId) return;
        api.get(`v1/apps/${appId}/pipelines/${pipelineId}/deal_fields/`)
            .then((r) => setFieldsConfig(r.data || []))
            .catch(console.error);
    }, [appId, pipelineId]);

    const save = async () => {
        if (!appId || !pipelineId) {
            setError('App ou Pipeline não encontrado - não é possível criar o negócio.');
            return;
        }
        if (!form.title) {
            setError('Título é obrigatório.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const visibleNames = new Set(fieldsConfig.filter((f) => isFieldVisible(f, fieldsConfig, fields)).map((f) => f.name));
            const fieldsToSend = Object.fromEntries(Object.entries(fields).filter(([name]) => visibleNames.has(name)));

            const payload = {
                deal: {
                    title: form.title,
                    value: parseFloat(form.value || 0),
                    description: form.description,
                    stage: firstStageId,
                    user: clienteSelecionado?.id || null,
                    organization: organizacaoSelecionada?.id || null,
                    assign: atribuidoSelecionado?.id || null,
                },
                fields: fieldsToSend,
            };

            const response = await api.post(`v1/apps/${appId}/pipelines/${pipelineId}/deals/`, payload);

            if (onSaved) onSaved();
            onClose();
            return response.data;
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Erro ao criar o negócio.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Novo Negócio
            </Typography>

            {error && (
                <Typography color="error" variant="body2" mb={2}>
                    {error}
                </Typography>
            )}

            <TextField name="title" label="Título" value={form.title} onChange={handleChange} fullWidth sx={{ mb: 2 }} />
            <TextField name="value" label="Valor R$" type="number" value={form.value} onChange={handleChange} fullWidth sx={{ mb: 2 }} />
            <TextField
                name="description"
                label="Descrição"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
            />

            {fieldsConfig.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {fieldsConfig
                        .filter((f) => isFieldVisible(f, fieldsConfig, fields))
                        .map((f) => (
                            <CustomFieldInput
                                key={f.id}
                                field={f}
                                value={fields[f.name]}
                                onChange={(value) => setFields({ ...fields, [f.name]: value })}
                            />
                        ))}
                </Grid>
            )}

            <Box display="flex" flexDirection="column" gap={2} mb={2}>
                <EntityPickerInline
                    label="Cliente"
                    placeholder="Buscar cliente por nome ou e-mail..."
                    searchUrl={(term) => `v1/apps/${appId}/users/?search=${term}`}
                    extractResults={(data) => data.users}
                    getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                    value={clienteSelecionado}
                    onSelect={setClienteSelecionado}
                    onClear={() => setClienteSelecionado(null)}
                    clearLabel="Sem cliente"
                />

                <EntityPickerInline
                    label="Organização"
                    placeholder="Buscar organização por nome ou CNPJ..."
                    searchUrl={(term) => `v1/apps/${appId}/organizations/?search=${term}`}
                    extractResults={(data) => data.organizations}
                    getOptionLabel={(o) => o.name}
                    value={organizacaoSelecionada}
                    onSelect={setOrganizacaoSelecionada}
                    onClear={() => setOrganizacaoSelecionada(null)}
                    clearLabel="Sem organização"
                />

                <EntityPickerInline
                    label="Atribuído"
                    placeholder="Buscar membro por nome ou e-mail..."
                    searchUrl={(term) => `users/?search=${term}`}
                    extractResults={(data) => data}
                    getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                    value={atribuidoSelecionado}
                    onSelect={setAtribuidoSelecionado}
                    onClear={() => setAtribuidoSelecionado(null)}
                    clearLabel="Sem atribuído"
                />
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={onClose} disabled={saving}>
                    Cancelar
                </Button>
                <Button onClick={save} variant="contained" disabled={saving}>
                    {saving ? 'Salvando...' : 'Criar Negócio'}
                </Button>
            </Box>
        </Box>
    );
};

export default NewDeal;
